import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, Linkedin, MessageCircle, Instagram, ArrowUpRight, Send } from "lucide-react";
import { PROFILE } from "../data/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast.success("Message received — I'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    }, 900);
  };

  const links = [
    { icon: Phone, label: "Call", value: PROFILE.phone, href: `tel:+${PROFILE.phoneRaw}`, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: PROFILE.email, href: `mailto:${PROFILE.email}`, testid: "contact-email" },
    { icon: MessageCircle, label: "WhatsApp", value: "Direct message", href: PROFILE.whatsapp, testid: "contact-whatsapp" },
    { icon: Instagram, label: "Instagram", value: PROFILE.instagramHandle, href: PROFILE.instagram, testid: "contact-instagram" },
    { icon: Linkedin, label: "LinkedIn", value: "Connect", href: PROFILE.linkedin, testid: "contact-linkedin" },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0B0E14] text-[#F2EBE5] py-24 md:py-36 overflow-hidden"
    >
      <img
        src="https://customer-assets.emergentagent.com/job_luxury-3d-style/artifacts/60dgynk6_9FD83664-4DE5-48FB-8B96-4936C1C6EDEE.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-[#0B0E14]/85 to-[#0B0E14]" />
      <div className="absolute inset-0 grain-overlay pointer-events-none opacity-50" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex items-center gap-3 text-[10px] tracking-overline uppercase text-[#D18A58] mb-6">
          <span className="h-px w-10 bg-[#D18A58]" />
          Get In Touch · 06
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.92] tracking-tight max-w-5xl"
        >
          Let's create something <span className="italic text-[#D18A58]">unforgettable</span>.
        </motion.h2>

        <div className="grid lg:grid-cols-12 gap-10 md:gap-16 mt-16 md:mt-20">
          <div className="lg:col-span-5 space-y-3">
            {links.map((l, i) => (
              <motion.a
                key={l.label}
                href={l.href}
                target={l.label === "LinkedIn" || l.label === "WhatsApp" || l.label === "Instagram" ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={l.testid}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group flex items-center justify-between gap-4 border-b border-white/10 py-5 hover:border-[#D18A58] transition-colors"
              >
                <div className="flex items-center gap-5 min-w-0">
                  <div className="h-11 w-11 shrink-0 rounded-full border border-white/15 flex items-center justify-center text-[#D18A58] group-hover:bg-[#D18A58] group-hover:text-[#0B0E14] transition-all">
                    <l.icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-overline text-[#8A8D98]">{l.label}</div>
                    <div className="font-display text-2xl md:text-3xl text-[#F2EBE5] truncate">{l.value}</div>
                  </div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-[#F2EBE5]/40 group-hover:text-[#D18A58] group-hover:rotate-0 -rotate-12 transition-all shrink-0" />
              </motion.a>
            ))}
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 lg:pl-8 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <FormField label="Name" testid="contact-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <FormField label="Email" type="email" testid="contact-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            </div>
            <FormField label="Subject" testid="contact-subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
            <FormField label="Message" textarea testid="contact-message" value={form.message} onChange={(v) => setForm({ ...form, message: v })} />

            <motion.button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={sending}
              whileTap={{ scale: 0.97 }}
              className="group inline-flex items-center gap-3 rounded-full bg-[#D18A58] px-7 py-4 text-sm uppercase tracking-overline text-[#0B0E14] font-medium hover:bg-[#F2EBE5] disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send Message"}
              <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </motion.button>
          </form>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, value, onChange, type = "text", textarea, testid }) {
  const [focused, setFocused] = useState(false);
  return (
    <label className="block relative">
      <span className={`block text-[10px] uppercase tracking-overline mb-2 transition-colors ${focused ? "text-[#D18A58]" : "text-[#8A8D98]"}`}>
        {label}
      </span>
      {textarea ? (
        <textarea
          data-testid={testid}
          value={value}
          rows={5}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full bg-transparent border-b border-white/15 focus:border-[#D18A58] outline-none py-3 text-base md:text-lg text-[#F2EBE5] resize-none transition-colors"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full bg-transparent border-b border-white/15 focus:border-[#D18A58] outline-none py-3 text-base md:text-lg text-[#F2EBE5] transition-colors"
        />
      )}
    </label>
  );
}
